import React from 'react';
import { useAtlas } from '../../context/AtlasContext';
import {
    CursorIcon,
    HandIcon,
    MapPinIcon,
    SquareIcon,
    PlusIcon,
    MinusIcon,
    ArrowsOutIcon,
    EraserIcon
} from '@phosphor-icons/react';
import { AtlasTool } from '../../types';

interface ToolButtonProps {
    tool: AtlasTool;
    currentTool: AtlasTool;
    onClick: () => void;
    icon: React.ReactNode;
    title: string;
}

const ToolButton: React.FC<ToolButtonProps> = ({ tool, currentTool, onClick, icon, title }) => (
    <button
        onClick={onClick}
        className={`p-3 rounded-xl transition-all ${
            currentTool === tool
                ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20 scale-105'
                : 'text-zinc-400 hover:text-white hover:bg-white/10'
        }`}
        title={title}
    >
        {icon}
    </button>
);

export const AtlasToolbar: React.FC = () => {
    const { tool, setTool, viewportState, setViewportState } = useAtlas();

    const handleZoomIn = () => {
        const newScale = Math.min(viewportState.scale * 1.25, 5);
        setViewportState({ ...viewportState, scale: newScale });
    };

    const handleZoomOut = () => {
        const newScale = Math.max(viewportState.scale * 0.8, 0.1);
        setViewportState({ ...viewportState, scale: newScale });
    };

    const handleFitToView = () => {
        setViewportState({ positionX: 0, positionY: 0, scale: 1 });
    };

    return (
        <div className="absolute left-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2 p-2 bg-zinc-900/80 backdrop-blur-md border border-white/10 rounded-2xl shadow-xl">
            {/* Selection Tools */}
            <ToolButton
                tool="select"
                currentTool={tool}
                onClick={() => setTool('select')}
                icon={<CursorIcon className="w-5 h-5" />}
                title="Select Tool (V)"
            />
            <ToolButton
                tool="pan"
                currentTool={tool}
                onClick={() => setTool('pan')}
                icon={<HandIcon className="w-5 h-5" />}
                title="Pan Tool (H)"
            />

            <div className="h-px w-8 bg-white/10 mx-auto my-1" />

            {/* Creation Tools */}
            <ToolButton
                tool="pin"
                currentTool={tool}
                onClick={() => setTool('pin')}
                icon={<MapPinIcon className="w-5 h-5" weight="duotone" />}
                title="Pin Tool - Create Location (P)"
            />
            <ToolButton
                tool="block"
                currentTool={tool}
                onClick={() => setTool('block')}
                icon={<SquareIcon className="w-5 h-5" weight="duotone" />}
                title="Block Tool - Expand Map (B)"
            />

            <div className="h-px w-8 bg-white/10 mx-auto my-1" />

            {/* Eraser Tool */}
            <ToolButton
                tool="eraser"
                currentTool={tool}
                onClick={() => setTool('eraser')}
                icon={<EraserIcon className="w-5 h-5" weight="duotone" />}
                title="Eraser Tool - Remove from Map (X)"
            />

            <div className="h-px w-8 bg-white/10 mx-auto my-1" />

            {/* Zoom Controls */}
            <button
                onClick={handleFitToView}
                className="p-3 text-zinc-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors"
                title="Fit to View"
            >
                <ArrowsOutIcon className="w-5 h-5" />
            </button>
            <button
                onClick={handleZoomIn}
                className="p-3 text-zinc-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors"
                title="Zoom In (+)"
            >
                <PlusIcon className="w-5 h-5" />
            </button>
            <button
                onClick={handleZoomOut}
                className="p-3 text-zinc-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors"
                title="Zoom Out (-)"
            >
                <MinusIcon className="w-5 h-5" />
            </button>

            {/* Zoom Level Indicator */}
            <div className="text-center text-xs text-zinc-500 mt-1">
                {Math.round(viewportState.scale * 100)}%
            </div>
        </div>
    );
};
